/**
 * Outward embedder notifications.
 *
 * The SPA notifies the embedding page (iframe parent or popup/new-tab opener)
 * with sanitized events:
 * - `LD_TURBOVETS_READY` / `LD_TURBOVETS_PREFILL_ACK` — the bidirectional
 *   readiness handshake (WI-TV-W2-002).
 * - `LD_TURBOVETS_COMPLETE` / `LD_TURBOVETS_ERROR` — submission outcomes
 *   (WI-TV-W1-006).
 *
 * All messages are posted ONLY to runtime-configured, validated origins with an
 * explicit `targetOrigin` (never `"*"`), and never contain borrower PII, field
 * names/values, raw request/response bodies, the API key/auth header, or stack
 * traces. On COMPLETE, `environment` is emitted only when the runtime debug
 * flag is on; the READY handshake always includes the environment label as
 * non-secret protocol metadata. The ERROR message is intentionally minimal:
 * only `type`, `correlationId`, and a single sanitized `reason` classification
 * (the high-level API error kind) — no status, partner reference, friendly
 * message, retryable flag, environment, or messageId (WI-TV-W3-007).
 *
 * The inbound `TURBOVETS_PREFILL` parsing/mapping in `autofill.ts` is unchanged;
 * this module only reports its outcome via the acknowledgement.
 */
import type { ApiErrorKind } from "./api";
import type { LmdSubmissionResult } from "./models/lmd";
import { PREFILL_MESSAGE_TYPE, type PrefillOutcome } from "./autofill";
import { absorbRejection } from "./callbacks";

export const MESSAGE_COMPLETE = "LD_TURBOVETS_COMPLETE";
export const MESSAGE_ERROR = "LD_TURBOVETS_ERROR";
export const MESSAGE_READY = "LD_TURBOVETS_READY";
export const MESSAGE_PREFILL_ACK = "LD_TURBOVETS_PREFILL_ACK";

/** Handshake protocol version advertised in the READY message. */
export const PROTOCOL_VERSION = 1 as const;

/** Inbound message types this SPA accepts from TurboVets. */
export const MESSAGE_TYPES_ACCEPTED: readonly string[] = [PREFILL_MESSAGE_TYPE];

/** Outbound message types this SPA can emit to the embedder. */
export const MESSAGE_TYPES_EMITTED: readonly string[] = [
  MESSAGE_READY,
  MESSAGE_PREFILL_ACK,
  MESSAGE_COMPLETE,
  MESSAGE_ERROR,
];

/**
 * Success event payload. Allowed fields only; `environment` is debug-only.
 *
 * Lead-only semantics: the SPA submits directly to LeadIntake and never creates
 * an Empower loan, so there is no `referralId`, loan number, or status/detail
 * link. A `leadGuid` is included from the LeadIntake `LDGUID`.
 */
export interface CompleteMessage {
  type: typeof MESSAGE_COMPLETE;
  messageId: string;
  correlationId: string;
  partnerReferenceId?: string;
  leadGuid?: string;
  status: string;
  submittedAt: string;
  environment?: string;
}

/**
 * Error event payload (WI-TV-W3-007). Narrowed to the final closeout contract:
 * it carries ONLY the message type, the correlation id, and one sanitized
 * `reason` classification derived from the high-level API error kind. It never
 * carries an exact status, partner reference, friendly end-user message,
 * retryable flag, debug environment, or messageId. Friendly messaging and
 * retry guidance stay inside the SPA UI; sanitized telemetry stays separate.
 */
export interface ErrorMessage {
  type: typeof MESSAGE_ERROR;
  correlationId: string;
  reason: ApiErrorKind;
}

export type EmbedderMessage = CompleteMessage | ErrorMessage;

/**
 * Readiness handshake payload (WI-TV-W2-002). Emitted once after the inbound
 * autofill listener is installed so embedders know when it is safe to send
 * `TURBOVETS_PREFILL`. Contains only non-secret protocol metadata.
 */
export interface ReadyMessage {
  type: typeof MESSAGE_READY;
  messageId: string;
  protocolVersion: typeof PROTOCOL_VERSION;
  environment: string;
  messageTypesAccepted: readonly string[];
  messageTypesEmitted: readonly string[];
}

export type PrefillAckStatus = "applied" | "ignored" | "rejected";
export type PrefillAckReason = "no-known-fields" | "invalid-shape";

/**
 * Acknowledgement for a handled inbound `TURBOVETS_PREFILL` (WI-TV-W2-002).
 * Reports the outcome without echoing any borrower field names or values.
 */
export interface PrefillAckMessage {
  type: typeof MESSAGE_PREFILL_ACK;
  messageId: string;
  correlationId?: string;
  status: PrefillAckStatus;
  reason?: PrefillAckReason;
  appliedFieldCount: number;
}

/** Any message this SPA posts outward to an embedder. */
export type OutboundMessage = EmbedderMessage | ReadyMessage | PrefillAckMessage;

/** Browser-safe, non-guessable id identifying a single outward message. */
export function createMessageId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface CompleteMessageInput {
  correlationId: string;
  response: LmdSubmissionResult;
  environment: string;
  debug: boolean;
  /** Partner reference from the submitted form (LMD does not echo it back). */
  partnerReferenceId?: string;
  messageId?: string;
}

/**
 * Builds a sanitized `LD_TURBOVETS_COMPLETE` payload from the LMD submission
 * result. Only contract-allowed, non-PII fields are copied across; the raw LMD
 * body is never forwarded.
 */
export function buildCompleteMessage(input: CompleteMessageInput): CompleteMessage {
  const message: CompleteMessage = {
    type: MESSAGE_COMPLETE,
    messageId: input.messageId ?? createMessageId(),
    correlationId: input.correlationId,
    status: input.response.status,
    submittedAt: input.response.submittedAt,
  };

  if (input.response.leadGuid) message.leadGuid = input.response.leadGuid;
  if (input.partnerReferenceId) message.partnerReferenceId = input.partnerReferenceId;

  if (input.debug) message.environment = input.environment;

  return message;
}

export interface ErrorMessageInput {
  correlationId: string;
  /** High-level, already-sanitized error classification (the API error kind). */
  reason: ApiErrorKind;
}

/**
 * Builds a sanitized `LD_TURBOVETS_ERROR` payload. Only the correlation id and
 * the high-level `reason` classification are ever emitted; no status, partner
 * reference, friendly message, retryable flag, environment, or messageId.
 */
export function buildErrorMessage(input: ErrorMessageInput): ErrorMessage {
  return {
    type: MESSAGE_ERROR,
    correlationId: input.correlationId,
    reason: input.reason,
  };
}

export interface ReadyMessageInput {
  environment: string;
  messageId?: string;
}

/**
 * Builds the `LD_TURBOVETS_READY` handshake payload. Carries only non-secret
 * protocol metadata: version, environment label, and the accepted/emitted
 * message-type lists. Never includes borrower PII, the API key, or payloads.
 */
export function buildReadyMessage(input: ReadyMessageInput): ReadyMessage {
  return {
    type: MESSAGE_READY,
    messageId: input.messageId ?? createMessageId(),
    protocolVersion: PROTOCOL_VERSION,
    environment: input.environment,
    messageTypesAccepted: [...MESSAGE_TYPES_ACCEPTED],
    messageTypesEmitted: [...MESSAGE_TYPES_EMITTED],
  };
}

export interface PrefillAckMessageInput {
  status: PrefillAckStatus;
  appliedFieldCount: number;
  reason?: PrefillAckReason;
  correlationId?: string;
  messageId?: string;
}

/** Builds a `LD_TURBOVETS_PREFILL_ACK` payload from explicit fields. */
export function buildPrefillAckMessage(input: PrefillAckMessageInput): PrefillAckMessage {
  const message: PrefillAckMessage = {
    type: MESSAGE_PREFILL_ACK,
    messageId: input.messageId ?? createMessageId(),
    status: input.status,
    appliedFieldCount: input.appliedFieldCount,
  };
  if (input.reason) message.reason = input.reason;
  if (input.correlationId !== undefined) message.correlationId = input.correlationId;
  return message;
}

/**
 * Maps an inbound prefill outcome to its acknowledgement message:
 * - `invalid-shape`   -> status "rejected", reason "invalid-shape"
 * - `no-known-fields` -> status "ignored",  reason "no-known-fields"
 * - otherwise (applied path) -> status "applied" with the applied field count.
 *
 * The `correlationId` (when the sender supplied one) is echoed back. No field
 * names or values are ever included. `disallowed-origin` outcomes must never be
 * passed here — the caller does not acknowledge disallowed origins.
 */
export function buildPrefillAck(outcome: PrefillOutcome, messageId?: string): PrefillAckMessage {
  if (outcome.reason === "invalid-shape") {
    return buildPrefillAckMessage({
      status: "rejected",
      reason: "invalid-shape",
      appliedFieldCount: 0,
      correlationId: outcome.correlationId,
      messageId,
    });
  }

  if (outcome.reason === "no-known-fields") {
    return buildPrefillAckMessage({
      status: "ignored",
      reason: "no-known-fields",
      appliedFieldCount: 0,
      correlationId: outcome.correlationId,
      messageId,
    });
  }

  return buildPrefillAckMessage({
    status: "applied",
    appliedFieldCount: outcome.fields.length,
    correlationId: outcome.correlationId,
    messageId,
  });
}

/**
 * Collects candidate embedder windows: the iframe parent and/or the popup
 * opener. The current window is never targeted.
 */
export function embedderTargets(win: Window): Window[] {
  const targets: Window[] = [];
  const parent = win.parent;
  if (parent && parent !== win) targets.push(parent);
  const opener = win.opener as Window | null;
  if (opener && opener !== win) targets.push(opener);
  return targets;
}

/**
 * Posts a message to each target window once per allowed origin, always with an
 * explicit `targetOrigin`. The browser only delivers a given post when the
 * target window's actual origin matches, so this fans out safely; when `*` is
 * explicitly configured for testing, a single wildcard `targetOrigin` is used.
 * Returns the number of `postMessage` calls made.
 */
export function postToTargets(
  targets: readonly (Window | null | undefined)[],
  message: OutboundMessage,
  allowedOrigins: readonly string[],
): number {
  if (allowedOrigins.length === 0) return 0;

  let count = 0;
  const seen = new Set<Window>();
  for (const target of targets) {
    if (!target || seen.has(target)) continue;
    seen.add(target);
    const targetOrigins = allowedOrigins.includes("*") ? ["*"] : allowedOrigins;
    for (const origin of targetOrigins) {
      try {
        // A host or test double can supply a `postMessage` that returns a
        // promise; its return value is absorbed rather than discarded so a
        // rejection cannot escape as an unhandled rejection.
        absorbRejection(target.postMessage(message, origin));
        count += 1;
      } catch {
        // Closed window or cross-origin access error: skip this target.
      }
    }
  }
  return count;
}

/**
 * Notifies the embedder of `win` (parent and/or opener) with `message`, posting
 * only to the validated `allowedOrigins`. No-op when there is no embedder or no
 * configured origins.
 */
export function notifyEmbedder(
  win: Window,
  message: OutboundMessage,
  allowedOrigins: readonly string[],
): number {
  return postToTargets(embedderTargets(win), message, allowedOrigins);
}
